import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconGift, IconChevronRight, IconCopy, IconCheck, IconX, IconTag } from '@tabler/icons-react';
import { thunkFetchClientCouponForCategory } from '../../../redux/slices/couponSlice';

const StickyCategoryCouponWidget = ({ categoryId, categoryName }) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [hasCheckedCoupons, setHasCheckedCoupons] = useState(false);

  const { 
    clientCategoryCoupons, 
    clientLoading, 
    clientError 
  } = useSelector((state) => state.coupon);

  // Fetch category coupons when component mounts or categoryId changes
  useEffect(() => {
    if (categoryId && !hasCheckedCoupons) {
      dispatch(thunkFetchClientCouponForCategory(categoryId))
        .finally(() => setHasCheckedCoupons(true));
    }
  }, [dispatch, categoryId, hasCheckedCoupons]);

  // Don't render if no coupons available or still loading
  if (clientLoading || !clientCategoryCoupons || clientCategoryCoupons.length === 0) {
    return null;
  }

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.coupon_type === 'percentage') {
      return `${coupon.discount_value}% OFF`;
    } else if (coupon.coupon_type === 'fixed_amount') {
      return `$${coupon.discount_value} OFF`;
    } else if (coupon.coupon_type === 'free_shipping') {
      return 'FREE SHIPPING';
    }
    return 'SPECIAL OFFER';
  };

  const formatExpiry = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="fixed left-0 top-1/2 transform -translate-y-1/2 z-40">
      {/* Collapsed State - Sticky Tab */}
      {!isExpanded && (
        <div
          onClick={() => setIsExpanded(true)}
          className="bg-gradient-to-r from-[#FFD500] to-[#FFA500] text-[#0B0C52] 
                     px-3 py-4 rounded-r-lg cursor-pointer shadow-lg hover:shadow-xl 
                     transition-all duration-300 hover:scale-105 group"
        >
          <div className="flex items-center gap-2">
            <IconGift size={20} className="animate-pulse" />
            <div className="writing-mode-vertical-rl text-sm font-bold">
              {clientCategoryCoupons.length} DEAL{clientCategoryCoupons.length > 1 ? 'S' : ''}
            </div>
            <IconChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </div>
          
          {/* Notification Badge */}
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs 
                          rounded-full w-6 h-6 flex items-center justify-center font-bold">
            {clientCategoryCoupons.length}
          </div>
        </div>
      )}

      {/* Expanded State - Coupon Panel */}
      {isExpanded && (
        <div className="bg-white rounded-r-lg shadow-2xl border-l-4 border-[#FFD500] 
                        w-80 max-h-96 overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0B0C52] to-[#002080] text-white p-4 rounded-tr-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IconTag size={20} />
                <div>
                  <h3 className="font-bold text-sm">Special Offers</h3>
                  <p className="text-xs opacity-90">{categoryName}</p>
                </div>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <IconX size={16} />
              </button>
            </div>
          </div>

          {/* Coupons List */}
          <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
            {clientCategoryCoupons.map((coupon, index) => (
              <div 
                key={coupon.id}
                className="border border-dashed border-[#FFD500] rounded-lg p-3 
                           bg-gradient-to-r from-[#FFD500]/10 to-[#FFA500]/10 relative"
              >
                {/* Best Deal Badge */}
                {index === 0 && (
                  <div className="absolute -top-2 -right-2 bg-[#0B0C52] text-white 
                                  text-xs px-2 py-1 rounded-full">
                    BEST
                  </div>
                )}

                <div className="space-y-2">
                  {/* Discount Info */}
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-[#0B0C52] text-lg">
                      {formatDiscount(coupon)}
                    </div>
                    <div className="text-xs text-gray-500">
                      Expires {formatExpiry(coupon.end_date)}
                    </div>
                  </div>

                  {/* Coupon Name */}
                  <div className="text-sm text-gray-600 font-medium">
                    {coupon.name}
                  </div>

                  {/* Code and Copy Button */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono font-bold text-[#0B0C52] text-sm 
                                    bg-white border-2 border-dashed border-[#0B0C52] 
                                    px-2 py-1 rounded flex-1 text-center">
                      {coupon.code}
                    </div>
                    <button
                      onClick={() => handleCopyCode(coupon.code)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                        copiedCode === coupon.code
                          ? 'bg-green-100 text-green-700'
                          : 'bg-[#0B0C52] text-white hover:bg-[#002080]'
                      }`}
                    >
                      {copiedCode === coupon.code ? (
                        <IconCheck size={12} />
                      ) : (
                        <IconCopy size={12} />
                      )}
                    </button>
                  </div>

                  {/* Usage Info */}
                  {coupon.max_uses && (
                    <div className="text-xs text-gray-500">
                      {coupon.current_uses || 0} of {coupon.max_uses} used
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t p-3 bg-gray-50 rounded-br-lg">
            <p className="text-xs text-gray-600 text-center">
              🎉 Exclusive deals for {categoryName} products!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StickyCategoryCouponWidget;